import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'

// Selectors
import {
  selectShowAVMDiffWarning,
  selectShowNonDisclosureStateWarning,
  selectShowLowConfidenceWarning
} from '../../../../store/selectors/home/metadata'
import { selectShouldRenderAvmHotMarketWarning } from '../../../../store/selectors/display'
import { hasCustomerToken } from '../../../../auth'
import { selectHomeData, selectHomeId } from '../../../../store/selectors/home'
import {
  selectValuationHistoryByHomeId,
  selectValuationGraphVisibilityByHomeId
} from '../../../../store/selectors/valuationHistory'
import { buildSegments } from '../../../../charts/lib/segmentBuilder'

// Actions
import { clientSnoozeAvmHotMarket } from '../../../../actions/client'
import { handleAcknowledgeClientHasSeenAvmDiff } from '../../../../actions/home'
import { handleAcknowledgeTuneValue } from '../../../../actions/homeowner'

// Components
import AvmDifferenceToolTipWarning from './ToolTipWarnings/AvmDifferenceToolTipWarning'
import LowConfidenceToolTipWarning from './ToolTipWarnings/LowConfidenceToolTipWarning'
import NonDisclosureToolTipWarning from './ToolTipWarnings/NonDisclosureToolTipWarning'
import AvmHotMarketToolTipWarning from './ToolTipWarnings/AvmHotMarketToolTipWarning'
import Tooltip from './Tooltip'

// Styles
import styles from './Chart.module.scss'
import { ValueLineChartWrapper } from './ValueLineChartWrapper'
import { AvmHistory } from '../../../../types/homePreview'
import { useTrackingContext } from '../../../../providers/tracking'

export const ChartWithCurrentHomeData = () => {
  const chartRef = useRef<SVGSVGElement | null>(null)
  const dispatch = useDispatch()
  const [forceHide, setForceHide] = useState(false)
  const { trackingClient } = useTrackingContext()

  const showAvmDiffWarning = useSelector(selectShowAVMDiffWarning)
  const showNonDisclosureWarning = useSelector(selectShowNonDisclosureStateWarning)
  const showLowConfidenceWarning = useSelector(selectShowLowConfidenceWarning)

  const showAvmHotMarketWarning = useSelector(selectShouldRenderAvmHotMarketWarning)

  const home = useSelector(selectHomeData)
  const homeId = useSelector(selectHomeId)
  const valuationHistory = useSelector(selectValuationHistoryByHomeId(homeId))
  const showChartWithCurrentHomeData = useSelector(selectValuationGraphVisibilityByHomeId(homeId))
  const data: AvmHistory = {
    segments: buildSegments(valuationHistory)
  }

  if (!showChartWithCurrentHomeData || !home) {
    return null
  }

  const renderChildren = () => {
    // ORDER DEPENDENT - DO NOT CHANGE
    if (showAvmHotMarketWarning) {
      return <AvmHotMarketToolTipWarning setForceHide={setForceHide} />
    }

    if (showNonDisclosureWarning) {
      return <NonDisclosureToolTipWarning setForceHide={setForceHide} />
    }

    if (showAvmDiffWarning) {
      return <AvmDifferenceToolTipWarning />
    }

    if (showLowConfidenceWarning) {
      return <LowConfidenceToolTipWarning setForceHide={setForceHide} />
    }
    return null
  }

  const getAction = () => {
    // ORDER DEPENDENT - DO NOT CHANGE
    if (showAvmHotMarketWarning) {
      return clientSnoozeAvmHotMarket
    }

    if (showNonDisclosureWarning || showLowConfidenceWarning) {
      return handleAcknowledgeTuneValue
    }

    if (showAvmDiffWarning) {
      return handleAcknowledgeClientHasSeenAvmDiff
    }
    return null
  }

  const handleClose = () => {
    trackingClient?.avmFeedback({ avm_alert_response: 'avm_dismissed' })

    setForceHide(true)
    if (hasCustomerToken()) return
    const action = getAction()
    if (action) {
      dispatch(action())
    }
  }

  const showWarning =
    showAvmHotMarketWarning || showNonDisclosureWarning || showAvmDiffWarning || showLowConfidenceWarning
  const showToolTip = showWarning && !forceHide

  return (
    <div className={styles.chartWrapper}>
      <div data-qa='valuation-chart' className={classnames(styles.container, showToolTip && styles.obfuscate)}>
        <ValueLineChartWrapper data={data} ref={chartRef} />

        {/* @ts-ignore */}
        <Tooltip data-qa='tool-tip' show={showToolTip} handleClose={handleClose} ref={chartRef}>
          {renderChildren()}
        </Tooltip>
      </div>
    </div>
  )
}
